import { ActionButton, IColumn, mergeStyles, Separator, Stack, Text } from '@fluentui/react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import ContentBox from '../../../ContentBox';
import { createFormSelector } from '../../slice';
import { FormVariables, Payment, Product } from '../../slice/newFormReducers';

export const FormContentBox = (props: {
  title: string;
  children: React.ReactNode;
}) => {
  const [visible, setVisible] = useState(true);

  return (
    <ContentBox>
      <Stack horizontal>
        <Text variant="xLarge">{props.title}</Text>
        <Stack grow horizontalAlign="end">
          <ActionButton
            styles={{ root: { height: "26px" } }}
            iconProps={{ iconName: visible ? "ChevronUp" : "ChevronDown" }}
            onClick={() => setVisible(!visible)}
          />
        </Stack>
      </Stack>
      {visible && (
        <>
          <Separator />
          {props.children}
        </>
      )}
    </ContentBox>
  );
};

export const detailsListNoPaddingStyles = mergeStyles({
  "& .ms-FocusZone": {
    paddingTop: "0 !important",
  },
});

export const connectRender =
  (callback: (uuid: string, column: IColumn) => any) =>
  (item?: any, index?: number, column?: IColumn) => {
    if (
      typeof item === "undefined" ||
      typeof index === "undefined" ||
      typeof column === "undefined"
    ) {
      return <></>;
    } else {
      // ! item: string uuid
      return callback(item, column);
    }
  };

export const useFormSummary = () => useSelector(formSummarySelector);

export const formSummarySelector = createFormSelector(
  (formVariables) => formVariables,
  (formVariables) => getSummary(formVariables)
);

export const getSummary = (formVariables: FormVariables) => {
  const totalPaidAmount = getPaidAmount(formVariables.payments);
  const totalProductCost = getTotalProductsPrice(formVariables.products);
  const discountAmount = getDiscountAmount(
    totalProductCost,
    formVariables.discount
  );

  const grandTotal = totalProductCost - discountAmount;
  const unpaidTotal = grandTotal - totalPaidAmount;

  return {
    totalPaidAmount,
    totalProductCost,
    discountAmount,
    grandTotal,
    unpaidTotal,
    hasRemainingFunds: unpaidTotal !== 0,
    version: "v2",
  };
};

export const getPaidAmount = (payments?: { [key: string]: Payment }) =>
  payments
    ? Object.keys(payments).reduce((a, b) => {
        const payment = payments[b];

        if (payment.isPaid && payment.amount && payment.amount > 0) {
          return a + payment.amount;
        } else {
          return a;
        }
      }, 0)
    : 0;

export const getDiscountAmount = (
  grandTotal: number,
  discount?: { currency?: number; percentage?: number }
) => {
  let discountAmount = 0;

  if (discount) {
    if (discount.currency) {
      discountAmount += discount.currency;
    }

    if (discount.percentage) {
      discountAmount += grandTotal * (discount.percentage / 100);
    }

    if (discountAmount > grandTotal) {
      discountAmount = grandTotal;
    }

    discountAmount = Math.ceil(discountAmount);
  }

  return discountAmount;
};

export const getTotalProductsPrice = (products?: { [key: string]: Product }) =>
  products
    ? Object.keys(products).reduce((a, b) => {
        const product = products[b];

        if (product.productInfo?.name && product.count && product.sellPrice) {
          return a + product.count * product.sellPrice;
        } else {
          return a;
        }
      }, 0)
    : 0;
